import { UserModel } from '~/scripts/services/model/user';
import kformUtils from '~/components/kform/utils/kform-utils';

/**
 * @param {any} item
 * @param {any[] | string} path
 * @returns {any|undefined}
 */
const getFromPath = (item, path) => {
	if (!item) {
		return undefined;
	}
	if (!path) {
		return item;
	}

	// If the path is a string, turn it into an array.
	if (typeof path === 'string') {
		// eslint-disable-next-line no-param-reassign
		path = path.split('.');
	}
	// If the path is an array, take the first element, and recurse its path
	if (Array.isArray(path)) {
		const current = path.shift();
		if (Object.prototype.hasOwnProperty.call(item, current)) {
			// If there are no more path items, stop here.
			if (path.length === 0) {
				return item[current];
			}

			return getFromPath(item[current], path);
		}
	}
	return item;
};

/**
 * @param {import('angular').IQService} $q
 * @param {import('angular').ITimeoutService} $timeout
 * @param {UserModel} User
 * @returns {any}
 */
const smartFieldInputDirective = /* @ngInject */ (
	$q,
	$timeout,
	User,
) => ({
	require: [
		'ngModel',
		'^smartField',
	],
	restrict: 'A',
	scope: true,
	link: (
		/** @type {any} */ scope,
		/** @type {any} */ element,
		/** @type {import('angular').IAttributes} */ attributes,
		/** @type {[any, any]} */ ctrls,
	) => {
		/**
		 * Minimal number of characters that needs to be entered before MF kicks-in
		 *
		 * @type {number}
		 */
		const minLength = 1;

		/**
		 * Minimal wait time after last character typed before MF kicks-in
		 *
		 * @type {number}
		 */
		const waitTime = 400;

		const [
			/**
			 * @type {Object}
			 */
			ngModelCtrl,

			/**
			 * @type {import('./smart-field.controller').default}
			 */
			smartFieldController,
		] = ctrls;

		/**
		 * Declare the timeout promise var outside the function scope so that stacked calls can be cancelled later
		 *
		 * @type {import('angular').IPromise<any>}
		 */
		let timeoutPromise;

		const scheduleSearchWithTimeout = (/** @type {string} */ inputValue) => {
			timeoutPromise = $timeout(() => smartFieldController.getMatches(inputValue), waitTime);
		};

		const cancelPreviousTimeout = () => {
			if (timeoutPromise) {
				$timeout.cancel(timeoutPromise);
			}
		};

		if (!scope.component) {
			scope.component = {
				key: `K${User.getRef()}`,
			};
		}

		if (ngModelCtrl) {
			// plug into $parsers pipeline to open a MF on view changes initiated from DOM
			// $parsers kick-in on all the changes coming from the view as well as manually triggered by $setViewValue
			ngModelCtrl.$parsers.unshift((/** @type {string | any} */ inputValue) => {
				// smartFieldController.hasFocus = true;
				if (typeof inputValue === 'object' && inputValue?.name) {
					return inputValue.name;
				}
				smartFieldController.message = undefined;

				if (
					smartFieldController.ngModelCtrl.$modelValue
					&& typeof smartFieldController.ngModelCtrl.$modelValue === 'object'
					&& 'kid' in smartFieldController.ngModelCtrl.$modelValue
					&& 'query' in smartFieldController.ngModelCtrl.$modelValue
					&& inputValue === smartFieldController.ngModelCtrl.$modelValue.query
				) {
					return ngModelCtrl.$modelValue;
				}

				if (minLength === 0 || (inputValue && inputValue.length >= minLength)) {
					if (waitTime > 0) {
						cancelPreviousTimeout();
						scheduleSearchWithTimeout(inputValue);
					}
					else {
						// noinspection JSIgnoredPromiseFromCall
						smartFieldController.getMatches(inputValue);
					}
				}
				else {
					smartFieldController.loading = false;
					cancelPreviousTimeout();
				}

				// let valid = true;
				//
				// if (mfCtrl.smartFieldValidate.minLength && inputValue.mfCtrl.smartFieldValidate < attrs.minLength) {
				// 	valid = false;
				// }
				//
				// if (mfCtrl.smartFieldValidate.maxLength && inputValue.length > mfCtrl.smartFieldValidate.maxLength) {
				// 	valid = false;
				// }
				//
				// ngModelCtrl.$setValidity('custom', valid);

				return inputValue;
			});

			if (smartFieldController.smartFieldValidate.minLength) {
				ngModelCtrl.$validators.minLength = (/** @type {any} */ _modelValue, /** @type {any} */ viewValue) => {
					if (typeof viewValue === 'object' && 'name' in viewValue) {
						// eslint-disable-next-line no-param-reassign
						viewValue = viewValue.name;
					}
					return ngModelCtrl.$isEmpty(viewValue)
						|| viewValue.length >= smartFieldController.smartFieldValidate.minLength;
				};
			}

			if (smartFieldController.smartFieldValidate.maxLength) {
				ngModelCtrl.$validators.maxLength = (/** @type {any} */ _modelValue, /** @type {any} */ viewValue) => {
					if (typeof viewValue === 'object' && 'name' in viewValue) {
						// eslint-disable-next-line no-param-reassign
						viewValue = viewValue.name;
					}
					return ngModelCtrl.$isEmpty(viewValue)
						|| viewValue.length <= smartFieldController.smartFieldValidate.maxLength;
				};
			}

			if (smartFieldController.smartFieldValidate.pattern) {
				ngModelCtrl.$validators.pattern = (/** @type {any} */ _modelValue, /** @type {any} */ viewValue) => {
					if (typeof viewValue === 'object' && 'name' in viewValue) {
						// eslint-disable-next-line no-param-reassign
						viewValue = viewValue.name;
					}
					return ngModelCtrl.$isEmpty(viewValue)
						|| new RegExp(smartFieldController.smartFieldValidate.pattern, 'g').test(viewValue);
				};
			}

			if (smartFieldController.smartFieldValidate.custom && smartFieldController.parent) {
				ngModelCtrl.$validators.custom = (/** @type {any} */ modelValue, /** @type {any} */ viewValue) => {
					let valid = true;
					/* eslint-disable no-unused-vars */
					// noinspection JSUnusedLocalSymbols
					const input = modelValue || viewValue; // jshint ignore:line

					// FOR-255 - Enable row data and form data to be visible in the validator.
					// noinspection JSUnusedLocalSymbols
					// const data = scope.submission.data; // jshint ignore:line
					// noinspection JSUnusedLocalSymbols
					// const row = scope.data; // jshint ignore:line
					/* eslint-enable no-unused-vars */

					let { custom } = smartFieldController.smartFieldValidate;
					custom = custom.replaceAll(/({{\s*(.*\S)\s*}})/g, (
						/** @type {any} */ _match,
						/** @type {any} */ _$1,
						/** @type {string | any[]} */ $2,
					) => getFromPath(
						smartFieldController.parent.forms[smartFieldController.currentKid].submission.data,
						$2,
					));

					try {
						// eslint-disable-next-line no-eval
						eval(custom);
					}
					catch (/** @type {any} */ error) {
						valid = error.message;
					}

					if (valid !== true) {
						scope.component.customError = valid;
						return false;
					}
					return true;
				};
			}
		}

		element.on('focus', (/** @type {InputEvent} */ event) => {
			smartFieldController.hasFocus = true;
			if (minLength === 0 && smartFieldController.ngModelCtrl && !smartFieldController.ngModelCtrl.$viewValue) {
				$timeout(() => smartFieldController.getMatches(smartFieldController.ngModelCtrl.$viewValue), 0);
			}
			if (event?.target) {
				// @ts-ignore
				event.target.select();
			}
			smartFieldController.mapIndex = -1;

			if (smartFieldController.oldMatches && smartFieldController.oldMapPath) {
				smartFieldController.matches = smartFieldController.oldMatches;
				smartFieldController.mapPath.push(...smartFieldController.oldMapPath);
				smartFieldController.active.length = 0;
				if (smartFieldController.ngModelCtrl) {
					$timeout(() => {
						scope.$apply(() => {
							// Reset validity as we are clearing
							smartFieldController.ngModelCtrl.$setValidity('editable', true);
							smartFieldController.ngModelCtrl.$setValidity('parse', true);
						});
					});
				}
			}
		});

		element.on('keyup cut copy paste', () => {
			if (element.val().trim() === '') {
				smartFieldController.ngModelCtrl.$setViewValue(undefined);
				smartFieldController.valid = true;
				smartFieldController.message = undefined;
			}
		});

		element.on('blur', () => {
			if (element.val().trim() === '') {
				smartFieldController.ngModelCtrl.$setViewValue(undefined);
			}
			// else if (smartFieldController.ngModel?.name !== element.val().trim()) {
			// 	smartFieldController.select({
			// 		isa:  smartFieldController.order,
			// 		name: element.val().trim(),
			// 	});
			// }
			smartFieldController.valid = true;
			smartFieldController.message = undefined;

			ngModelCtrl.$setValidity('editable', true);
			ngModelCtrl.$setValidity('parse', true);
			ngModelCtrl.$validate();

			if (smartFieldController.smartFieldEvent && 'onChange' in smartFieldController.smartFieldEvent) {
				const callback = smartFieldController.smartFieldEvent.onChange;
				if (callback in smartFieldController.MethodService.methods) {
					smartFieldController.MethodService.methods[callback](ngModelCtrl.$modelValue);
				}
				else if (typeof callback === 'string' && smartFieldController.parent) {
					const custom = ''.concat(callback, '; ');
					kformUtils.evaluate(custom, {
						data: smartFieldController.parent.forms[smartFieldController.parent.currentKid].submission.data,
						form: smartFieldController.parent.forms[smartFieldController.parent.currentKid],
					});
				}
			}

			smartFieldController.hasFocus = false;
		});

		scope.$on('$destroy', () => {
			element.off('focus keyup cut copy paste blur');
		});
	},
});

export default smartFieldInputDirective;
